/**
 * Maps app roles to analytics "personas" and which dashboard widgets each persona should see,
 * aligned with product intent (Town / FEMA share the general "network" view until dedicated roles exist).
 */

export const ANALYTICS_PERSONA = {
  ADMIN: "admin",
  AIRPORT: "airport",
  NONPROFIT: "nonprofit",
  PILOT: "pilot",
  /** User, guest, and future Town / FEMA — community & coordination metrics without admin/airport tools */
  NETWORK: "network",
};

/**
 * @param {string} [role] - Prefer `auth.tokenRole` (JWT after profile switch); fallback `auth.profile.role`.
 * @returns {keyof typeof ANALYTICS_PERSONA}
 */
export function resolveAnalyticsPersona(role) {
  const r = String(role || "").toLowerCase();
  if (r === "admin") return ANALYTICS_PERSONA.ADMIN;
  if (r === "airport") return ANALYTICS_PERSONA.AIRPORT;
  if (r === "nonprofit") return ANALYTICS_PERSONA.NONPROFIT;
  if (r === "pilot") return ANALYTICS_PERSONA.PILOT;
  return ANALYTICS_PERSONA.NETWORK;
}

/**
 * Which hero KPI cards to show (keys into `heroMetricLibrary` in analyticsDummyData).
 */
const HERO_KEYS = {
  [ANALYTICS_PERSONA.ADMIN]: ["decline", "churn", "conversion", "lives"],
  [ANALYTICS_PERSONA.AIRPORT]: ["response", "lives", "missions", "repeat"],
  [ANALYTICS_PERSONA.NONPROFIT]: ["categoriesSummary", "repeat", "lives", "pilotScore"],
  [ANALYTICS_PERSONA.PILOT]: ["nonprofitScore", "lives", "missionsFlown", "response"],
  [ANALYTICS_PERSONA.NETWORK]: ["response", "decline", "lives", "categoriesSummary"],
};

/**
 * Feature flags per persona (from Interested Personas matrix).
 */
const SECTIONS = {
  [ANALYTICS_PERSONA.ADMIN]: {
    impactOverTime: true,
    missionCategoryTags: false,
    declineDoughnut: true,
    repeatPilots: true,
    pilotReliabilityRadar: true,
    nonprofitReliabilityRadar: true,
    conversionFunnel: true,
    churnChart: true,
    heatMap: true,
    operatingStatesMap: true,
    airportOperations: false,
  },
  [ANALYTICS_PERSONA.AIRPORT]: {
    impactOverTime: true,
    missionCategoryTags: false,
    declineDoughnut: false,
    repeatPilots: true,
    pilotReliabilityRadar: false,
    nonprofitReliabilityRadar: false,
    conversionFunnel: false,
    churnChart: false,
    heatMap: true,
    operatingStatesMap: true,
    airportOperations: true,
  },
  [ANALYTICS_PERSONA.NONPROFIT]: {
    impactOverTime: true,
    missionCategoryTags: true,
    declineDoughnut: false,
    repeatPilots: true,
    pilotReliabilityRadar: true,
    nonprofitReliabilityRadar: false,
    conversionFunnel: false,
    churnChart: false,
    heatMap: true,
    operatingStatesMap: true,
    airportOperations: false,
  },
  [ANALYTICS_PERSONA.PILOT]: {
    impactOverTime: true,
    missionCategoryTags: false,
    declineDoughnut: false,
    repeatPilots: false,
    pilotReliabilityRadar: false,
    nonprofitReliabilityRadar: true,
    conversionFunnel: false,
    churnChart: false,
    heatMap: true,
    operatingStatesMap: true,
    airportOperations: false,
  },
  [ANALYTICS_PERSONA.NETWORK]: {
    impactOverTime: true,
    missionCategoryTags: true,
    declineDoughnut: true,
    repeatPilots: false,
    pilotReliabilityRadar: false,
    nonprofitReliabilityRadar: false,
    conversionFunnel: false,
    churnChart: false,
    heatMap: true,
    operatingStatesMap: true,
    airportOperations: false,
  },
};

const PERSONA_INTRO = {
  [ANALYTICS_PERSONA.ADMIN]: {
    eyebrow: "Admin",
    title: "Network operations",
    subtitle:
      "Fill rates, pilot and nonprofit reliability, conversion, churn, and geographic reach across AviPair.",
  },
  [ANALYTICS_PERSONA.AIRPORT]: {
    eyebrow: "Airport",
    title: "Field & community impact",
    subtitle:
      "Response times, missions you support, lives touched, repeat pilots, coverage maps, and partner organizations.",
  },
  [ANALYTICS_PERSONA.NONPROFIT]: {
    eyebrow: "Nonprofit",
    title: "Programs & pilot partners",
    subtitle:
      "Mission categories you run, pilot reliability signals, repeat participation, and impact over time.",
  },
  [ANALYTICS_PERSONA.PILOT]: {
    eyebrow: "Pilot",
    title: "Missions & partners",
    subtitle:
      "Nonprofit coordination quality, your impact, and where help is available on the map.",
  },
  [ANALYTICS_PERSONA.NETWORK]: {
    eyebrow: "Community",
    title: "Mission activity & coverage",
    subtitle:
      "Response and fill rates, mission types, impact trends, and where nonprofits operate — for towns, agencies, and the public.",
  },
};

export function getPersonaDashboardConfig(persona) {
  const p = SECTIONS[persona] ? persona : ANALYTICS_PERSONA.NETWORK;
  return {
    persona: p,
    heroKeys: HERO_KEYS[p] || HERO_KEYS[ANALYTICS_PERSONA.NETWORK],
    sections: SECTIONS[p] || SECTIONS[ANALYTICS_PERSONA.NETWORK],
    intro: PERSONA_INTRO[p] || PERSONA_INTRO[ANALYTICS_PERSONA.NETWORK],
  };
}
